import { defineStore } from "pinia";

import { computed, ref } from "vue";
import { useChatStore } from "@/stores/chat/chat.store";
import {
  type ChatBloDto,
  RequestStatus,
  type ChatDto,
  type PaginationConfig,
  type UserChatFilterDto,
  REQUEST_ICON_BY_STATUS,
  USER_REQUEST_NAME_BY_STATUS,
  type AddMessageDto,
  type ChatIdsFilterDto,
} from "@/api/api.dto";

export const useSupportUserStore = defineStore("useSupportUserStore", () => {
  const chatStore = useChatStore();
  const excludedStatuses = ref<RequestStatus[]>([
    RequestStatus.NEW,
    RequestStatus.ANSWERED,
  ]);

  const showAllRequests = ref<boolean>(false);

  const requestsFilter = computed<
    Partial<Record<RequestStatus, UserChatFilterDto>>
  >(() => ({
    [RequestStatus.IN_WORK]: {
      skip:
        (pagination.value[RequestStatus.IN_WORK]!.current - 1) *
        pagination.value[RequestStatus.IN_WORK]!.pageSize,
      take: pagination.value[RequestStatus.IN_WORK]!.pageSize,
      allForOwner: showAllRequests.value,
      isClosed: false,
      isOrderByDesc: true,
    },
    [RequestStatus.CLOSED]: {
      skip:
        (pagination.value[RequestStatus.CLOSED]!.current - 1) *
        pagination.value[RequestStatus.CLOSED]!.pageSize,
      take: pagination.value[RequestStatus.CLOSED]!.pageSize,
      allForOwner: showAllRequests.value,
      isClosed: true,
      isOrderByDesc: true,
    },
  }));

  const requestsIdsFilter = computed<
    Partial<Record<RequestStatus, ChatIdsFilterDto>>
  >(() => ({
    [RequestStatus.IN_WORK]: {
      allForOwner: showAllRequests.value,
      isClosed: false,
      isOrderByDesc: true,
    },
    [RequestStatus.CLOSED]: {
      allForOwner: showAllRequests.value,
      isClosed: true,
      isOrderByDesc: true,
    },
  }));

  const requests = ref<Partial<Record<RequestStatus, ChatBloDto[]>>>({
    [RequestStatus.IN_WORK]: [],
    [RequestStatus.CLOSED]: [],
  });

  const requestsIds = ref<Partial<Record<RequestStatus, number[]>>>({
    [RequestStatus.IN_WORK]: [],
    [RequestStatus.CLOSED]: [],
  });

  const getUserChatsByStatus = (status: RequestStatus) => {
    return async (_page: number, _pageSize: number) => {
      const requestsInfo: ChatDto | null | undefined =
        await chatStore.fetchUserChats(
          requestsFilter.value[status] as UserChatFilterDto,
        );
      requests.value[status] = requestsInfo?.chats || [];
      pagination.value[status]!.total = requestsInfo?.count || 0;
    };
  };

  const getUserChatsIdsByStatus = async (status: RequestStatus) => {
    const ids: number[] = await chatStore.getExistenRequestsIds(
      requestsIdsFilter.value[status] as ChatIdsFilterDto,
    );
    requestsIds.value[status] = ids || [];
  };

  const pagination = ref<Partial<Record<RequestStatus, PaginationConfig>>>({
    [RequestStatus.IN_WORK]: {
      current: 1,
      pageSize: 5,
      defaultPageSize: 5,
      total: 0,
      showQuickJumper: false,
      showSizeChanger: false,
      showLessItems: true,
      pageSizeOptions: [5],
      onChangePagination: getUserChatsByStatus(RequestStatus.IN_WORK),
    },
    [RequestStatus.CLOSED]: {
      current: 1,
      pageSize: 5,
      defaultPageSize: 5,
      total: 0,
      showQuickJumper: false,
      showSizeChanger: false,
      showLessItems: true,
      pageSizeOptions: [5],
      onChangePagination: getUserChatsByStatus(RequestStatus.CLOSED),
    },
  });

  const mapRequest = (chat: ChatBloDto, status: RequestStatus) => ({
    id: chat.id,
    icon: REQUEST_ICON_BY_STATUS[status],
    title: `${chat.applicationNumber}: ${chat.title || "Без темы"}`,
    status,
  });

  const groupedRequestsByStatus = computed(() => {
    return Object.entries(requests.value)
      .filter(
        ([status]) =>
          !excludedStatuses.value.map(Number).includes(Number(status)),
      )
      .map(([status, requests], index) => ({
        statusName: USER_REQUEST_NAME_BY_STATUS[
          status as unknown as RequestStatus
        ] as string,
        status: +status as RequestStatus,
        id: index.toFixed(),
        requests: requests.map((r) =>
          mapRequest(r, status as unknown as RequestStatus),
        ),
      }));
  });

  const fetchAllUserChats = async () => {
    await Promise.all([
      getUserChatsByStatus(RequestStatus.IN_WORK)(
        pagination.value[RequestStatus.IN_WORK]!.current,
        pagination.value[RequestStatus.IN_WORK]!.pageSize,
      ),
      getUserChatsByStatus(RequestStatus.CLOSED)(
        pagination.value[RequestStatus.CLOSED]!.current,
        pagination.value[RequestStatus.CLOSED]!.pageSize,
      ),
    ]);
  };

  const fetchAllUserChatsIds = async () => {
    await Promise.all([
      getUserChatsIdsByStatus(RequestStatus.IN_WORK),
      getUserChatsIdsByStatus(RequestStatus.CLOSED),
    ]);
  };

  const initialize = async () => {
    await fetchAllUserChats();
    await chatStore.connectWebSocket();
  };

  const sendFirstMessage = async (messageDTO: AddMessageDto) => {
    return await chatStore.sendFirstMessage(messageDTO);
  };

  return {
    initialize,
    sendFirstMessage,
    fetchAllUserChats,
    fetchAllUserChatsIds,
    requests,
    requestsIds,
    groupedRequestsByStatus,
    pagination,
    showAllRequests,
  };
});
